import { sbEditable } from '@storyblok/storyblok-editable';
import classNames from 'classnames';
import { ReactElement, RefObject, useMemo, WheelEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { CaseCarouselProps } from './CaseCarousel.data';
import styles from './CaseCarousel.module.scss';
import { setupTransitionInTimeline, setupTransitionOutTimeline } from './CaseCarousel.transitions';
import { useChangeTransitionState } from 'util/react-transition-component/hooks/useChangeTransitionState';
import { useTransitionController } from 'util/react-transition-component';
import { useDraggable } from '../../../hooks/useDraggable';
import Heading from '../../atoms/heading/Heading';
import gsap from 'gsap';
import useWindowSize from '../../../hooks/useWindowSize';
import useThemeScrollBlock from '../../../hooks/useThemeScrollBlock';
import type { ThemeColorProviderContextType } from '../../unlisted/ThemeColorProvider';
import { useThemeColorProviderContext } from '../../unlisted/ThemeColorProvider';
import type { ScrollProviderContextType } from '../../unlisted/ScrollProvider';
import { useScrollProviderContext } from '../../unlisted/ScrollProvider';
import CaseCard from 'components/blocks/case-card/CaseCard';
import type { Card } from 'util/storyblok/types/global.types';
import { CaseCardRef } from 'components/blocks/case-card/CaseCard.data';
import { useGlobalStore } from '@/stores/global';
import Icon from 'components/atoms/icon/Icon';

// TODO: make it the only CaseCarousel and remove V2 and V1

export default function CaseCarousel({ className, blok, ref }: CaseCarouselProps): ReactElement {
  const localRef = useRef(null);
  const elementRef = (ref || localRef) as RefObject<HTMLDivElement | null>;
  const sliderRef = useRef<HTMLDivElement>(null);
  const caseCardsRef = useRef<Array<CaseCardRef>>([]);
  const progressBarContainerRef = useRef<HTMLDivElement>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const draggableBoundsRef = useRef<HTMLDivElement>(null);
  const buttonsContainerRef = useRef<HTMLDivElement>(null);

  const draggableInstance = useRef<Draggable | null>(null);

  const [isLeftArrowDisabled, setLeftArrowDisabled] = useState(true);
  const [isRightArrowDisabled, setRightArrowDisabled] = useState(false);

  const { lock, unLock } = useScrollProviderContext() as ScrollProviderContextType;

  const onDrag = useCallback(() => {
    if (!draggableInstance?.current) return;

    const { x, minX, maxX } = draggableInstance.current;
    const progress = gsap.utils.normalize(maxX, minX, x);

    setLeftArrowDisabled(x >= maxX);
    setRightArrowDisabled(x <= minX);

    if (progressBarRef.current) {
      gsap.set(progressBarRef.current, {
        left: `${100 * progress}%`,
        translateX: `${-100 * progress}%`,
      });
    }
  }, [draggableInstance]);

  draggableInstance.current = useDraggable(
    sliderRef,
    {
      type: 'x',
      edgeResistance: 0.8,
      lockAxis: true,
      inertia: true,
      cursor: 'grab',
      onDrag,
      onThrowUpdate: onDrag,
    },
    {
      bounds: draggableBoundsRef,
    },
  );

  const boundWidth = draggableBoundsRef?.current?.getBoundingClientRect().width || 0;
  const sliderWidth = sliderRef?.current?.getBoundingClientRect().width || 0;

  if (draggableInstance.current) {
    if (boundWidth >= sliderWidth) {
      draggableInstance.current.disable();
    } else {
      draggableInstance.current.enable();
    }
  }

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;

    if (!element) {
      setIsVisible(false);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px -40% 0px',
      },
    );

    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, [elementRef]);

  const transitionController = useTransitionController(
    () => ({
      ref: elementRef,
      refs: {
        elementRef,
        caseCardsRef,
        buttonsContainerRef,
      },
      setupTransitionInTimeline,
      setupTransitionOutTimeline,
    }),
    [draggableInstance.current],
  );

  useChangeTransitionState(transitionController, {
    onMount: false,
    value: isVisible,
  });

  const resizeProgressBar = () => {
    if (!progressBarRef.current || !progressBarContainerRef.current) return;

    const visibleBounds = draggableBoundsRef?.current?.getBoundingClientRect();
    const sliderBounds = sliderRef?.current?.getBoundingClientRect();

    if (visibleBounds && sliderBounds) {
      gsap.set(progressBarRef.current, {
        width: `${Math.min(1, visibleBounds.width / sliderBounds.width) * 100}%`,
      });
      gsap.set(progressBarContainerRef.current, {
        visibility: visibleBounds.width < sliderBounds.width ? 'visible' : 'hidden',
      });
    }
  };

  const { width: windowWidth } = useWindowSize();

  useEffect(resizeProgressBar, [
    draggableBoundsRef,
    sliderRef,
    progressBarRef,
    progressBarContainerRef,
    windowWidth,
  ]);

  const handleScroll = (e: WheelEvent) => {
    const absDeltaX = Math.abs(e.deltaX);
    const absDeltaY = Math.abs(e.deltaY);
    const isHorizontalDominant = absDeltaX > 0 && absDeltaX > absDeltaY;

    if (isHorizontalDominant) {
      lock();
    } else {
      unLock();
      return;
    }

    if (draggableInstance.current) {
      const { minX, maxX } = draggableInstance.current;
      const currentX: number = parseInt(gsap.getProperty(sliderRef.current, 'x') as string, 10);
      const newX = currentX - e.deltaX;

      const x = Math.min(maxX, Math.max(minX, newX));
      setLeftArrowDisabled(x === maxX);
      setRightArrowDisabled(x === minX);
      gsap.set(sliderRef.current, { x });

      const progress = gsap.utils.normalize(maxX, minX, newX);

      if (progressBarRef.current) {
        gsap.set(progressBarRef.current, {
          left: `${100 * progress}%`,
          translateX: `${-100 * progress}%`,
        });
      }
    }
  };

  const gridGutterRef = useRef<HTMLDivElement>(null);
  const gapBetweenImagesRef = useRef<HTMLDivElement>(null);

  useThemeScrollBlock(elementRef, true, {
    foreground: '#000',
    background: '#fff',
  });

  const { isDarkTheme } = useThemeColorProviderContext() as ThemeColorProviderContextType;

  const isDark = (isDarkTheme && !blok.use_accent_color) || (!isDarkTheme && blok.use_accent_color);
  const isLight =
    (!isDarkTheme && !blok.use_accent_color) || (isDarkTheme && blok.use_accent_color);

  const emptyValues = {
    currentX: 0,
    minX: 0,
    maxX: 0,
    width: 0,
  };
  const getSliderValues = () => {
    if (!caseCardsRef?.current?.[0] || !draggableInstance.current) return emptyValues;
    const currentX = gsap.getProperty(sliderRef.current, 'x') as number;
    const imageWidth = gsap.getProperty(
      caseCardsRef.current[0].mediaRef.current,
      'width',
    ) as number;

    const gridGapWidth = gsap.getProperty(gapBetweenImagesRef.current, 'width') as number;
    const width = imageWidth + gridGapWidth;
    const { minX, maxX } = draggableInstance.current;

    return { currentX, minX, maxX, width };
  };

  const handleNext = () => {
    const { minX, currentX, width } = getSliderValues();
    const x = Math.max(
      minX,
      (Math.floor(currentX / width) - (currentX % width < 1 ? 1 : 0)) * width,
    );
    setRightArrowDisabled(x === minX);
    setLeftArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const handlePrevious = () => {
    const { maxX, currentX, width } = getSliderValues();
    const x = Math.min(
      maxX,
      (Math.ceil(currentX / width) + (currentX % width < 1 ? 1 : 0)) * width,
    );
    setLeftArrowDisabled(x === maxX);
    setRightArrowDisabled(false);

    gsap.to(sliderRef.current, { x });
  };

  const { minX, maxX } = getSliderValues();

  const store = useGlobalStore();
  const { countryCode } = store;

  const geoFilterCards = useMemo(() => {
    let filteredCards: Array<Card> = [];
    const cards = blok?.cards;
    if (countryCode) {
      filteredCards = cards?.filter((cardItem) => {
        if (typeof cardItem.case === 'string') {
          // if the resolve_relations is not resolved
          return true;
        }

        if (cardItem.case.content.countries?.length > 0) {
          if (cardItem.case.content.exclude_countries) {
            return !cardItem.case.content.countries?.includes(countryCode);
          } // else
          return cardItem.case.content.countries?.includes(countryCode);
        }
        return true;
      });
    } else {
      return cards;
    }

    return filteredCards;
  }, [blok?.cards, countryCode]);

  return (
    <div
      ref={elementRef}
      className={classNames(styles.imageSliderDraggable, className)}
      {...sbEditable(blok)}
    >
      {/* The 2 divs below are here to get --grid-gutter and --grid-gap values in js */}
      <div className={styles.gridGutterWidth} ref={gridGutterRef} />
      <div className={styles.gapBetweenImages} ref={gapBetweenImagesRef} />

      <div className={classNames(styles.titleContainer, styles.flexContainer)}>
        {blok.title && (
          <Heading
            as="h3"
            size="heading-03"
            transitionInType="splitCharsSlideInUp"
            disableTriggerInOnScroll={false}
            className={styles.title}
          >
            {blok.title.split('\n').map((line, index) => (
              <div key={`${line}-${index}`}>{line}</div>
            ))}
          </Heading>
        )}

        {draggableInstance.current && maxX !== minX && (
          <div
            className={classNames(styles.buttonsContainer, {
              [styles.lightTheme]: isLight,
              [styles.darkTheme]: isDark,
            })}
            ref={buttonsContainerRef}
          >
            <button
              className={styles.arrowButton}
              disabled={isLeftArrowDisabled}
              onClick={handlePrevious}
            >
              <Icon name="arrow-left-light" className={classNames(styles.icon)} />
            </button>

            <button
              className={styles.arrowButton}
              disabled={isRightArrowDisabled}
              onClick={handleNext}
            >
              <Icon name="arrow-right-light" className={classNames(styles.icon)} />
            </button>
          </div>
        )}

        {/* {allImagesUploaded && ( */}
        {/*   <div ref={progressBarContainerRef} className={styles.progressContainer}> */}
        {/*     <div ref={progressBarRef} className={styles.progressBar} /> */}
        {/*   </div> */}
        {/* )} */}
      </div>

      <div ref={draggableBoundsRef} className={styles.draggableBounds}>
        <div ref={sliderRef} className={styles.slider} onWheel={handleScroll} onMouseLeave={unLock}>
          {geoFilterCards?.map((card, index) => (
            <CaseCard
              ref={(ref: CaseCardRef) => {
                caseCardsRef.current[index] = ref;
              }}
              isCarousel
              imageInnerTransition={undefined}
              className={classNames(styles.card)}
              blok={card}
              key={card._uid}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
